import React, { useEffect, useState } from "react";
import { Copy, Check, Loader2, EyeOff, Landmark, Eye } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

// Lot 759 — « Guide de versement » : sur un retrait à verser (approuvé / versement en cours), les coordonnées déchiffrées
// du bénéficiaire (révélation journalisée côté serveur) + un bouton « Copier le virement » (texte prêt à coller dans la
// banque : bénéficiaire, IBAN groupé, BIC, montant, communication YALOLA-RET-XXXXXXXX) qui pré-remplit aussi la référence
// de l'action « Payé ». Garde-fou `withdrawal_payment_guide` (OFF → bouton « Révéler » manuel du lot 652).
export const withdrawalReference = (w) => `YALOLA-RET-${String(w?.id || "").slice(0, 8).toUpperCase()}`;
export const groupIban = (iban) => String(iban || "").replace(/\s+/g, "").replace(/(.{4})/g, "$1 ").trim();
const fmtAmount = (n, cur) => `${(Math.round((Number(n) || 0) * 100) / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} ${cur || "EUR"}`;

export const paymentLines = (w, method, details) => {
  const d = details || {};
  const lines = [];
  if (d.holder_name) lines.push(["Bénéficiaire", d.holder_name]);
  if (method?.type === "bank") {
    if (d.iban) lines.push(["IBAN", groupIban(d.iban)]);
    if (d.bic) lines.push(["BIC", d.bic]);
    if (d.bank_name) lines.push(["Banque", d.bank_name]);
  } else if (method?.type === "paypal") {
    if (d.email) lines.push(["PayPal", d.email]);
  } else if (method?.type === "mobile_money") {
    if (d.operator) lines.push(["Opérateur", d.operator]);
    if (d.phone) lines.push(["Numéro", d.phone]);
  } else if (d.raw) lines.push(["Coordonnées", d.raw]);
  lines.push(["Montant", fmtAmount(w?.amount, w?.currency)]);
  lines.push(["Communication", withdrawalReference(w)]);
  return lines;
};
export const paymentText = (w, method, details) => [`Virement Yalola — retrait ${String(w?.id || "").slice(0, 8).toUpperCase()}`, ...paymentLines(w, method, details).map(([k, v]) => `${k} : ${v}`)].join("\n");

const copyText = async (text) => {
  try { await navigator.clipboard.writeText(text); return true; } catch { /* fallback */ }
  try {
    const ta = document.createElement("textarea"); ta.value = text; ta.setAttribute("readonly", ""); ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.select(); const ok = document.execCommand("copy"); ta.remove(); return ok;
  } catch { return false; }
};

export const WithdrawalPaymentGuide = ({ w, method, canManage = true, onUseReference }) => {
  const [details, setDetails] = useState(null);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState("");
  const [hidden, setHidden] = useState(false);
  const load = () => {
    setErr(null);
    api.get(`/admin/payout-methods/${method.id}/reveal`).then(({ data }) => setDetails(data.payout_method?.details || {})).catch((e) => setErr(e?.friendlyMessage || e?.response?.data?.detail || "Révélation impossible"));
  };
  useEffect(() => { if (method?.id && canManage) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [method?.id]);
  if (!method?.id) return null;
  const lines = details ? paymentLines(w, method, details) : [];
  const copy = async (what, text) => {
    const ok = await copyText(text);
    if (!ok) { toast.error("Copie impossible — sélectionne le texte à la main."); return; }
    setCopied(what); setTimeout(() => setCopied(""), 2000);
    toast.success(what === "all" ? "Virement copié — colle-le dans ta banque" : `${what} copié`);
    if (what === "all") onUseReference?.(withdrawalReference(w));
  };
  return (
    <div className="rounded-xl p-3 space-y-2" style={{ background: "#FFFBEB", border: "1px solid #FDE68A", color: "#78350F" }} data-testid="withdrawal-payment-guide" data-status={w?.status}>
      <div className="flex items-center gap-2 flex-wrap">
        <Landmark size={14} />
        <span className="text-[10px] uppercase font-bold tracking-wide">Guide de versement — à payer maintenant</span>
        {details && !hidden && <button type="button" className="ml-auto text-[11px] underline inline-flex items-center gap-1" onClick={() => setHidden(true)} data-testid="withdrawal-payment-guide-hide"><EyeOff size={11} /> Masquer</button>}
        {details && hidden && <button type="button" className="ml-auto text-[11px] underline inline-flex items-center gap-1" onClick={() => setHidden(false)} data-testid="withdrawal-payment-guide-show"><Eye size={11} /> Afficher</button>}
      </div>
      {!canManage && <p className="text-xs">Réservé au super admin et aux managers.</p>}
      {canManage && !details && !err && <p className="text-xs inline-flex items-center gap-1" data-testid="withdrawal-payment-guide-loading"><Loader2 size={12} className="animate-spin" /> Déchiffrement des coordonnées…</p>}
      {err && <p className="text-xs" data-testid="withdrawal-payment-guide-error">{err} <button type="button" className="underline ml-1" onClick={load}>Réessayer</button></p>}
      {details && !hidden && (
        <>
          <div className="text-xs space-y-1" data-testid="withdrawal-payment-guide-lines">
            {lines.map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 flex-wrap" data-testid={`withdrawal-payment-line-${k.toLowerCase().replace(/[^a-z]/g, "")}`}>
                <span className="w-28 shrink-0 text-[10px] uppercase font-bold tracking-wide opacity-80">{k}</span>
                <span className="font-mono select-all break-all">{v}</span>
                {["IBAN", "PayPal", "Numéro", "Communication", "Montant"].includes(k) && (
                  <button type="button" title={`Copier ${k}`} onClick={() => copy(k, String(v).replace(/\s+/g, k === "IBAN" ? "" : " "))} className="opacity-70 hover:opacity-100" data-testid={`withdrawal-payment-copy-${k.toLowerCase()}`}>
                    {copied === k ? <Check size={12} /> : <Copy size={12} />}
                  </button>
                )}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap pt-1">
            <Button size="sm" className="pill-primary rounded-full" onClick={() => copy("all", paymentText(w, method, details))} data-testid="withdrawal-payment-copy-all">
              {copied === "all" ? <Check size={13} className="mr-1" /> : <Copy size={13} className="mr-1" />} Copier le virement
            </Button>
            <span className="text-[11px] opacity-80">Colle dans ta banque, puis « Payé » avec la communication pré-remplie comme référence.</span>
          </div>
        </>
      )}
    </div>
  );
};

export default WithdrawalPaymentGuide;
